import { DataTable } from '@app/components/data-table/data-table';
import { DataTableToolbar } from '@app/components/data-table/data-table-toolbar';
import { useServerDataTable } from '@app/components/data-table/hooks/use-server-data-table';
import { useServerFilterOptions } from '@app/components/data-table/hooks/use-server-filter-options';
import { DataTableProps } from '@app/components/data-table/types/data-table';
import { PaginatedOperationReportListList } from '@shared/api/types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@shared/components/ui/select';
import { cn } from '@shared/lib/utils';
import { Table } from '@tanstack/react-table';
import { parseAsInteger, useQueryState } from 'nuqs';
import { generatePath, useNavigate } from 'react-router-dom';
import { ReportType } from '../types';
import { getReportsPathMap } from '../utils';
import ReportCard from './report-card';
import { ReportFilterColumn, reportFilterKeys } from './reports-filters';
import { useReportTableColumns } from './reports-table-columns';

const SORT_OPTIONS = [
  {
    value: 'created:desc',
    label: 'Date: Newest first',
    id: 'created',
    desc: true,
  },
  {
    value: 'created:asc',
    label: 'Date: Oldest first',
    id: 'created',
    desc: false,
  },
  { value: 'title:asc', label: 'Name: A–Z', id: 'title', desc: false },
  { value: 'title:desc', label: 'Name: Z–A', id: 'title', desc: true },
  {
    value: 'emissions:desc',
    label: 'Emissions: High to Low',
    id: 'emissions',
    desc: true,
  },
  {
    value: 'emissions:asc',
    label: 'Emissions: Low to High',
    id: 'emissions',
    desc: false,
  },
];

function ReportsTableSortDropdown({ table }: { table: Table<any> }) {
  const sorting = table.getState().sorting;
  const current = sorting[0];
  const currentValue = current
    ? `${current.id}:${current.desc ? 'desc' : 'asc'}`
    : undefined;

  return (
    <Select
      value={currentValue}
      onValueChange={(value) => {
        const option = SORT_OPTIONS.find((o) => o.value === value);
        if (option) {
          table.setSorting([{ id: option.id, desc: option.desc }]);
        }
      }}
    >
      <SelectTrigger className="h-8 w-auto space-x-1.5 border-dashed text-xs font-medium">
        <SelectValue placeholder="Sort by" />
      </SelectTrigger>
      <SelectContent>
        {SORT_OPTIONS.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

interface ReportsTableProps
  extends DataTableProps<PaginatedOperationReportListList> {
  type?: 'table' | 'card';
  reportType: ReportType;
}

export default function ReportsTable({
  type,
  reportType,
  data,
  isLoading,
}: ReportsTableProps) {
  const navigate = useNavigate();
  const [perPage] = useQueryState('perPage', parseAsInteger.withDefault(10));

  const { results: reports, count } = data || {};
  const pageCount = count ? Math.ceil(count / perPage) : 0;

  const filterResult = useServerFilterOptions(
    reportFilterKeys,
    data?.filters || {}
  );
  const filterOptions = {
    tags: filterResult.tagsFilter ?? [],
    sites: filterResult.sitesFilter ?? [],
    timePeriods: filterResult.timePeriodsFilter ?? [],
    customers: filterResult.customersFilter ?? [],
  };

  const columns = useReportTableColumns({ filters: filterOptions, reportType });

  const { table, context } = useServerDataTable({
    data: reports || [],
    columns,
    pageCount,
    initialState: {
      columnVisibility: {
        uuid: false,
        [ReportFilterColumn.Tags]: false,
        [ReportFilterColumn.Sites]: false,
        [ReportFilterColumn.TimePeriods]: false,
        [ReportFilterColumn.Customers]: false,
        [ReportFilterColumn.GoodsType]: false,
        created: false,
        emissions: false,
      },
    },
    getRowId: (row) => row.uuid,
  });

  return (
    <DataTable
      table={table}
      context={context}
      showHeader={type === 'card' ? false : true}
      isLoading={isLoading}
      onRowClick={(row) => {
        const uuid = row?.original.uuid;
        navigate(
          generatePath(getReportsPathMap()[reportType].report, {
            report_uuid: uuid,
          })
        );
      }}
      rowClassName={() => cn('', { 'bg-gray-25 border-none': type === 'card' })}
      customRowItem={
        type === 'card'
          ? (row) => (
              <ReportCard
                report={row.original}
                reportType={reportType}
                className="mb-3 cursor-pointer"
              />
            )
          : undefined
      }
    >
      <DataTableToolbar table={table}>
        <ReportsTableSortDropdown table={table} />
      </DataTableToolbar>
    </DataTable>
  );
}
